import { useState, useMemo, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, Loader2, AlertTriangle } from 'lucide-react';
import useSWR from 'swr';
import { useAuthStore } from '../../hooks/useAuth';
import { getWarehouseLocalComponents, resolveEffectiveTimeZone } from '../../utils/timezoneDisplay';
import { generateOperatingSlots, OperatingSlot } from '../../utils/generateOperatingSlots';
import { DateTime } from 'luxon';

interface Slot {
    start: Date;
    end: Date;
    isBlocked: boolean;
    availableDocks: number;
    totalDocks: number;
    appointmentId?: string;
    opSlot: OperatingSlot;
}

// Selected slot state retains canonical UTC occurrence to distinguish DST fall-back repeated hours.
interface SelectedSlotState {
    start: string;    // local wall-clock "YYYY-MM-DDTHH:mm" for display/submission
    end: string;      // local wall-clock "YYYY-MM-DDTHH:mm" for display/submission
    startUtc: string; // canonical UTC ISO — unique occurrence key (disambiguates fall-back)
    endUtc: string;   // canonical UTC ISO
}

interface CalendarSlotPickerProps {
    onSelect: (start: string, end: string, startUtc?: string, endUtc?: string) => void;
    warehouseId: string;
    initialDate?: Date;
}

const fetcher = async ([url, token]: [string, string]) => {
    const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error('Failed to fetch schedule');
    return res.json();
};

export function CalendarSlotPicker({ onSelect, warehouseId, initialDate = new Date() }: CalendarSlotPickerProps) {
    const { token } = useAuthStore();
    const [selectedSlot, setSelectedSlot] = useState<SelectedSlotState | null>(null);

    const { data: warehouseData, isLoading: warehouseLoading } = useSWR(
        token && warehouseId ? [`/api/warehouses/${warehouseId}`, token] : null,
        fetcher
    );

    const whTz = warehouseData?.warehouse?.timeZone || warehouseData?.warehouse?.timezone;

    const [selectedLocalDate, setSelectedLocalDate] = useState<string>('');

    // Synchronize initial browser date safely to the warehouse timezone calendar day.
    // Never derive the warehouse calendar date from browser-local Date getters.
    useEffect(() => {
        if (whTz && !selectedLocalDate) {
            setSelectedLocalDate(getWarehouseLocalComponents(initialDate.toISOString(), whTz).date);
        }
    }, [whTz, initialDate, selectedLocalDate]);

    // Fetch existing appointments
    const { data: apptData, isLoading: apptsLoading } = useSWR(
        token && warehouseId ? [`/api/appointments/universal?filter=upcoming`, token] : null,
        fetcher
    );

    // Fetch docks to know total capacity
    const { data: dockData } = useSWR(
        token && warehouseId ? [`/api/warehouses/${warehouseId}/docks`, token] : null,
        fetcher
    );

    const appointments = apptData?.appointments || [];
    const operationalDockCount = useMemo(() => {
        const list = dockData?.docks || [];
        const active = list.filter((d: any) => {
            if (d.isActive === false) return false;
            const s = String(d.status || '').trim().toLowerCase();
            if (s === 'maintenance' || s === 'oos' || s.includes('out of service') || s.includes('out_of_service')) {
                return false;
            }
            return true;
        });
        return active.length || list.length || 1;
    }, [dockData]);
    const dockCount = operationalDockCount;

    // Filter to only ACTIVE appointments (exclude cancelled/completed)
    const activeAppointments = useMemo(() => {
        const ACTIVE = new Set(['pending', 'approved', 'checked_in', 'in_progress']);
        return appointments.filter((a: any) => ACTIVE.has((a.status || '').toLowerCase()));
    }, [appointments]);

    // Derive authoritative config from warehouse — no hardcoded fallbacks.
    const wh = warehouseData?.warehouse;
    const openTime: string | undefined = wh?.openTime;
    const closeTime: string | undefined = wh?.closeTime;
    const slotMinutes: number | undefined = wh?.slotMinutes;

    // Determine render state:
    //   'loading'   — warehouse data has not arrived yet
    //   'invalid'   — data arrived but required scheduling config is absent
    //   'ready'     — all required config is present
    const configState: 'loading' | 'invalid' | 'ready' = (() => {
        if (warehouseLoading || !warehouseData) return 'loading';
        if (!whTz || !openTime || !closeTime || !slotMinutes || !selectedLocalDate) return 'invalid';
        return 'ready';
    })();

    // Generate Slots — only runs when configState === 'ready'
    const slots = useMemo(() => {
        if (configState !== 'ready') return [];
        // All guards below are redundant (configState === 'ready' implies they hold),
        // but TypeScript narrowing requires them.
        if (!whTz || !openTime || !closeTime || !slotMinutes || !selectedLocalDate) return [];

        const canonicalSlots = generateOperatingSlots(selectedLocalDate, whTz, openTime, closeTime, slotMinutes);

        const generatedSlots: Slot[] = [];

        // Calculate real closing UTC boundary.
        //
        // Ambiguity note: DateTime.fromObject with a wall-clock time that falls in the
        // fall-back repeated hour is resolved by Luxon to the FIRST (pre-transition) occurrence,
        // which is the same rule generateOperatingSlots uses for its endDt.
        // Both computations therefore agree on the closing boundary, preserving consistency
        // with the canonical operating-window semantics established in generateOperatingSlots.
        const [closeH, closeM] = closeTime.split(':').map(Number);
        const [openH, openM] = openTime.split(':').map(Number);
        const [year, month, day] = selectedLocalDate.split('-').map(Number);

        const effectiveCloseTz = resolveEffectiveTimeZone(whTz, selectedLocalDate) || whTz;
        let closeDt = DateTime.fromObject(
            { year, month, day, hour: closeH, minute: closeM },
            { zone: effectiveCloseTz }
        );
        // Overnight shift: close wall-clock is numerically ≤ open wall-clock → close is next calendar day.
        if ((closeH * 60 + closeM) <= (openH * 60 + openM)) {
            closeDt = closeDt.plus({ days: 1 });
        }
        const closeUtcEpoch = closeDt.toJSDate().getTime();

        for (const op of canonicalSlots) {
            const start = new Date(op.startUtc);
            const durationMs = 7200000; // exactly 2 hours
            const end = new Date(start.getTime() + durationMs);

            // Reject slot if the 2-hour booking would run past the warehouse closing boundary.
            if (end.getTime() > closeUtcEpoch) continue;

            let isBlocked = false;
            let maxActiveCount = 0;

            // Check availability across every slotMinutes-interval within the 2-hour booking.
            // Collision test is on absolute UTC epochs — correct across DST and overnight.
            for (let offset = 0; offset < durationMs; offset += slotMinutes * 60000) {
                const checkTime = new Date(start.getTime() + offset);

                const activeCount = activeAppointments.filter((appt: any) => {
                    const apptStart = new Date(appt.startAt);
                    const apptEnd = new Date(appt.endAt);
                    return apptStart <= checkTime && apptEnd > checkTime;
                }).length;

                if (activeCount > maxActiveCount) {
                    maxActiveCount = activeCount;
                }

                if (activeCount >= dockCount) {
                    isBlocked = true;
                    break;
                }
            }

            const availableDocks = isBlocked ? 0 : Math.max(0, dockCount - maxActiveCount);

            generatedSlots.push({
                start,
                end,
                isBlocked,
                availableDocks,
                totalDocks: dockCount,
                opSlot: op,
            });
        }
        return generatedSlots;
    }, [configState, selectedLocalDate, activeAppointments, dockCount, openTime, closeTime, slotMinutes, whTz]);

    const handleDateChange = (days: number) => {
        if (!selectedLocalDate || !whTz) return;
        // Always advance the warehouse-local calendar date using Luxon in the warehouse zone.
        setSelectedLocalDate(prev => {
            const effectiveTz = resolveEffectiveTimeZone(whTz, prev) || whTz;
            return DateTime.fromISO(prev, { zone: effectiveTz }).plus({ days }).toISODate()!;
        });
        setSelectedSlot(null);
        onSelect('', '', '', '');
    };

    const handleSlotClick = (slot: Slot) => {
        if (slot.isBlocked) return;

        const startStr = `${slot.opSlot.localDate}T${slot.opSlot.localTime}`;
        const startUtcStr = slot.opSlot.startUtc;  // canonical occurrence key

        if (!whTz) return;
        const effectiveEndTz = resolveEffectiveTimeZone(whTz, slot.end) || whTz;
        const endDt = DateTime.fromJSDate(slot.end).setZone(effectiveEndTz);
        const endStr = endDt.toFormat("yyyy-MM-dd'T'HH:mm");
        const endUtcStr = slot.end.toISOString();

        setSelectedSlot({ start: startStr, end: endStr, startUtc: startUtcStr, endUtc: endUtcStr });
        onSelect(startStr, endStr, startUtcStr, endUtcStr);
    };

    // ── Render ────────────────────────────────────────────────────────────────

    // Still loading warehouse data — show a spinner.
    if (configState === 'loading') {
        return (
            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg overflow-hidden">
                <div className="flex justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-primary" />
                </div>
            </div>
        );
    }

    // Warehouse data arrived but required scheduling config is missing — show an error rather than
    // an infinite spinner, so the UI does not silently hang on a permanently broken config.
    if (configState === 'invalid') {
        return (
            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg overflow-hidden">
                <div className="flex flex-col items-center gap-2 py-8 px-4 text-center">
                    <AlertTriangle className="w-5 h-5 text-amber-500" />
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                        Scheduling configuration unavailable.
                        Please ask your administrator to configure the warehouse operating hours.
                    </p>
                </div>
            </div>
        );
    }

    // configState === 'ready' — all guards passed, render the picker.
    // whTz and selectedLocalDate are guaranteed non-empty here.
    return (
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg overflow-hidden">
            {/* Header: Date Navigation */}
            <div className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/50">
                <button
                    type="button"
                    onClick={(e) => { e.preventDefault(); handleDateChange(-1); }}
                    className="p-1 hover:bg-slate-200 dark:hover:bg-slate-800 rounded text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white transition-colors"
                >
                    <ChevronLeft className="w-5 h-5" />
                </button>
                <div className="flex items-center gap-2 font-semibold text-slate-800 dark:text-slate-200">
                    <CalendarIcon className="w-4 h-4 text-primary" />
                    {/* Explicit warehouse zone to avoid implicit browser-zone behaviour on a date-only string. */}
                    {DateTime.fromISO(selectedLocalDate, { zone: resolveEffectiveTimeZone(whTz, selectedLocalDate) || whTz }).toFormat('ccc, MMMM d')}
                </div>
                <button
                    type="button"
                    onClick={(e) => { e.preventDefault(); handleDateChange(1); }}
                    className="p-1 hover:bg-slate-200 dark:hover:bg-slate-800 rounded text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white transition-colors"
                >
                    <ChevronRight className="w-5 h-5" />
                </button>
            </div>

            {/* Slot Grid */}
            <div className="p-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 max-h-[400px] overflow-y-auto custom-scrollbar">
                {apptsLoading ? (
                    <div className="col-span-full flex justify-center py-8">
                        <Loader2 className="w-6 h-6 animate-spin text-primary" />
                    </div>
                ) : (
                    slots.map((slot) => {
                        const timeLabel = slot.opSlot.displayLabel;

                        // Selection identity uses canonical startUtc — unique even for DST fall-back
                        // repeated hours that share identical localDate/localTime strings.
                        const isSelected = selectedSlot?.startUtc === slot.opSlot.startUtc;

                        return (
                            <button
                                key={slot.opSlot.startUtc}
                                type="button"
                                onClick={(e) => { e.preventDefault(); handleSlotClick(slot); }}
                                disabled={slot.isBlocked}
                                className={`
                                    flex flex-col items-center justify-center p-3 rounded-lg border transition-all relative overflow-hidden group
                                    ${isSelected
                                        ? 'bg-blue-50 dark:bg-primary/20 border-blue-400 dark:border-primary text-blue-700 dark:text-primary ring-1 ring-blue-400 dark:ring-primary'
                                        : slot.isBlocked
                                            ? 'bg-slate-100 dark:bg-slate-900/50 border-slate-200 dark:border-slate-800 opacity-50 cursor-not-allowed'
                                            : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 hover:border-blue-300 dark:hover:border-slate-600 hover:bg-blue-50/50 dark:hover:bg-slate-800'
                                    }
                                `}
                            >
                                <span className={`text-sm font-bold ${isSelected ? 'text-blue-700 dark:text-white' : slot.isBlocked ? 'text-slate-400 dark:text-slate-500' : 'text-slate-800 dark:text-slate-300'}`}>
                                    {timeLabel}
                                </span>

                                {slot.isBlocked ? (
                                    <div className="absolute top-1 right-1 w-2 h-2 rounded-full bg-red-500/60" title="All Docks Occupied" />
                                ) : slot.availableDocks < slot.totalDocks ? (
                                    <div
                                        className="absolute top-1 right-1 w-2 h-2 rounded-full bg-amber-500"
                                        title={`${slot.availableDocks} of ${slot.totalDocks} docks open`}
                                    />
                                ) : null}

                                <div
                                    className={`mt-1 text-[10px] uppercase tracking-wider ${
                                        slot.isBlocked
                                            ? 'text-red-500/70 dark:text-red-400/70 font-semibold'
                                            : slot.availableDocks < slot.totalDocks
                                                ? 'text-amber-600 dark:text-amber-400 font-semibold'
                                                : 'text-emerald-600 dark:text-emerald-400'
                                    }`}
                                >
                                    {slot.isBlocked
                                        ? `Full (0/${slot.totalDocks})`
                                        : slot.availableDocks < slot.totalDocks
                                            ? `${slot.availableDocks} of ${slot.totalDocks} Docks`
                                            : `Open (${slot.availableDocks}/${slot.totalDocks})`}
                                </div>
                            </button>
                        );
                    })
                )}
            </div>

            {/* Footer Summary */}
            {selectedSlot && (
                <div className="border-t border-slate-200 dark:border-slate-800 p-3 bg-blue-50 dark:bg-primary/5 text-center">
                    <p className="text-xs text-primary font-medium">
                        Selected: {DateTime.fromISO(selectedSlot.startUtc).setZone(resolveEffectiveTimeZone(whTz, selectedSlot.startUtc) || whTz).toFormat('h:mm a')} - {DateTime.fromISO(selectedSlot.endUtc).setZone(resolveEffectiveTimeZone(whTz, selectedSlot.endUtc) || whTz).toFormat('h:mm a')}
                    </p>
                </div>
            )}
        </div>
    );
}
